import { LANGUAGES, LANGUAGE_CODES, PAYMENT_BANK } from 'Douleutaras/constants/system';

export enum SITE_COUNTRY_CODE {
  GR = 'GR',
  CY = 'CY',
  IE = 'IE',
  PT = 'PT'
}

export enum SITE_ID {
  GR = 1,
  CY = 3,
  IE = 10,
  PT = 20
}

// Matches domains like *.douleutaras.gr
export const SITE_COUNTRY_CODE_REGEX = {
  GR: '^.+\\.douleutaras\\.(gr|com)$',
  CY: '^.+\\.douleutaras\\.com\\.cy$',
  IE: '^.+\\.yourpro\\.ie$',
  PT: '^.+\\.yourhero\\.pt$'
};

const generateAppLink = ({ ibi,
                           isi,
                           apn, link }: { ibi: string, isi: string, apn: string, link: string }) => {
  return 'https://douleutarascustomers.page.link/' +
    `?link=${link}&apn=${apn}&ibi=${ibi}&isi=${isi}`;
};

const generateApiUrl = () => {
  if (typeof window === 'undefined') {
    return {
      [SITE_COUNTRY_CODE.GR]: process.env.API_URL_GR,
      [SITE_COUNTRY_CODE.CY]: process.env.API_URL_CY,
      [SITE_COUNTRY_CODE.IE]: process.env.API_URL_IE,
      [SITE_COUNTRY_CODE.PT]: process.env.API_URL_PT
    };
  }

  const { hostname, origin } = window.location;
  const isDevOrTest = hostname.includes('localhost')
    || hostname.includes('test-react')
    || hostname.includes('local.douleutaras');
  const baseOrigin = `${origin}/`;

  // Helper to determine the URL based on the environment
  const getUrl = (envUrl?: string) => (isDevOrTest ? envUrl : baseOrigin);

  return {
    [SITE_COUNTRY_CODE.GR]: getUrl(process.env.API_URL_GR),
    [SITE_COUNTRY_CODE.CY]: getUrl(process.env.API_URL_CY),
    [SITE_COUNTRY_CODE.IE]: getUrl(process.env.API_URL_IE),
    [SITE_COUNTRY_CODE.PT]: getUrl(process.env.API_URL_PT)
  };
};

export interface CityBoundsCollection {
  [city: string]: {
    SOUTH: number;
    WEST: number;
    NORTH: number;
    EAST: number;
  };
}

export const CITY_BOUNDS: CityBoundsCollection = {
  ATHENS: {
    SOUTH: 37.7934,
    WEST:  23.6533,
    NORTH: 38.0756,
    EAST: 23.8896
  }
};

export const COUNTRY_CONFIG = {
  // Restricts auto-complete for External Post Job "address" field
  EXTERNAL_JOB_AUTOCOMPLETE: {
    [SITE_COUNTRY_CODE.GR]: CITY_BOUNDS.ATHENS
  },
  GTM_CONTAINER_ID: {
    [SITE_COUNTRY_CODE.GR]: process.env.GTM_CONTAINER_ID_GR,
    [SITE_COUNTRY_CODE.CY]: process.env.GTM_CONTAINER_ID_CY,
    [SITE_COUNTRY_CODE.IE]: process.env.GTM_CONTAINER_ID_IE,
    [SITE_COUNTRY_CODE.PT]: process.env.GTM_CONTAINER_ID_PT
  },
  MAP_CENTER: {
    [SITE_COUNTRY_CODE.GR]: { lat: 38.0603, lng: 23.7275 },
    [SITE_COUNTRY_CODE.CY]: { lat: 35.095192, lng: 33.20343 },
    [SITE_COUNTRY_CODE.IE]: { lat: 53.35014, lng: -6.266155 },
    [SITE_COUNTRY_CODE.PT]: { lat: 38.736946, lng: -9.142685 }
  },
  // Site's name
  SITE_NAME: {
    [SITE_COUNTRY_CODE.GR]: 'Douleutaras',
    [SITE_COUNTRY_CODE.CY]: 'Douleutaras',
    [SITE_COUNTRY_CODE.IE]: 'YourPro',
    [SITE_COUNTRY_CODE.PT]: 'YourHero'
  },
  // Site's name URL
  SITE_URL_LABEL: {
    [SITE_COUNTRY_CODE.GR]: 'Douleutaras.gr',
    [SITE_COUNTRY_CODE.CY]: 'Douleutaras.com.cy',
    [SITE_COUNTRY_CODE.IE]: 'YourPro.ie',
    [SITE_COUNTRY_CODE.PT]: 'YourHero.pt'
  },
  // Template for <title>
  SITE_META_TITLE_TEMPLATE: {
    [SITE_COUNTRY_CODE.GR]: '%s | Douleutaras',
    [SITE_COUNTRY_CODE.CY]: '%s | Douleutaras',
    [SITE_COUNTRY_CODE.IE]: '%s | YourPro',
    [SITE_COUNTRY_CODE.PT]: '%s | YourHero'
  },
  API_URL: generateApiUrl(),
  LANGUAGES_SUPPORTED: {
    [SITE_COUNTRY_CODE.GR]: [ LANGUAGES.EL.CODE, LANGUAGES.EN.CODE ],
    [SITE_COUNTRY_CODE.CY]: [ LANGUAGES.EL_CY.CODE, LANGUAGES.EN.CODE ],
    [SITE_COUNTRY_CODE.IE]: [ LANGUAGES.EN_IE.CODE ],
    [SITE_COUNTRY_CODE.PT]: [ LANGUAGES.PT.CODE, LANGUAGES.EN_PT.CODE ]
  },
  DEFAULT_LANGUAGE: {
    [SITE_COUNTRY_CODE.GR]: LANGUAGES.EL.CODE,
    [SITE_COUNTRY_CODE.CY]: LANGUAGES.EL_CY.CODE,
    [SITE_COUNTRY_CODE.IE]: LANGUAGES.EN_IE.CODE,
    [SITE_COUNTRY_CODE.PT]: LANGUAGES.PT.CODE
  },
  CLEANING_CATEGORY: {
    [SITE_COUNTRY_CODE.GR]: 356,
    [SITE_COUNTRY_CODE.CY]: 762,
    [SITE_COUNTRY_CODE.IE]: undefined,
    [SITE_COUNTRY_CODE.PT]: 1305
  },
  ONLINE_PAYMENT_BANK: {
    [SITE_COUNTRY_CODE.GR]: [PAYMENT_BANK.STRIPE, PAYMENT_BANK.SEPA],
    [SITE_COUNTRY_CODE.CY]: [PAYMENT_BANK.STRIPE, PAYMENT_BANK.SEPA],
    [SITE_COUNTRY_CODE.IE]: [PAYMENT_BANK.STRIPE, PAYMENT_BANK.SEPA],
    [SITE_COUNTRY_CODE.PT]: [PAYMENT_BANK.STRIPE, PAYMENT_BANK.SEPA]
  },
  ONLINE_PAYMENT_UNATTENDED: {
    [SITE_COUNTRY_CODE.GR]: PAYMENT_BANK.STRIPE_UNATTENDED,
    [SITE_COUNTRY_CODE.CY]: PAYMENT_BANK.STRIPE_UNATTENDED,
    [SITE_COUNTRY_CODE.IE]: PAYMENT_BANK.STRIPE_UNATTENDED,
    [SITE_COUNTRY_CODE.PT]: PAYMENT_BANK.STRIPE_UNATTENDED
  },

  MOBILE_APP_LINK: {
    [SITE_COUNTRY_CODE.GR]: generateAppLink({
      link: 'https%3A%2F%2Fwww.douleutaras.gr',
      apn: 'com.yourhero.mobile.customers',
      ibi: 'com.yourhero.mobile.customers',
      isi: '1587680274'
    }),
    [SITE_COUNTRY_CODE.CY]: generateAppLink({
      link: 'https%3A%2F%2Fwww.douleutaras.com.cy',
      apn: 'com.yourhero.mobile.customers',
      ibi: 'com.yourhero.mobile.customers',
      isi: '1587680274'
    }),
    [SITE_COUNTRY_CODE.IE]: generateAppLink({
      link: 'https%3A%2F%2Fwww.yourpro.ie',
      ibi: 'com.yourhero.mobile.customers.irish',
      apn: 'com.yourhero.mobile.customers.irish',
      isi: '1614231111'
    }),
    [SITE_COUNTRY_CODE.PT]: generateAppLink({
      link: 'https%3A%2F%2Fwww.yourhero.pt',
      ibi: 'com.yourhero.mobile.customers.portugal',
      apn: 'com.yourhero.mobile.customers.portugal',
      isi: '1614229004'
    })
  },
  MOMENT: {
    TIMEZONE: {
      [SITE_COUNTRY_CODE.GR]: 'Europe/Athens',
      [SITE_COUNTRY_CODE.CY]: 'Asia/Nicosia',
      [SITE_COUNTRY_CODE.IE]: 'Europe/Dublin',
      [SITE_COUNTRY_CODE.PT]: 'Europe/Lisbon'
    }
  },
  PHONE_REGEXP: {
    [SITE_COUNTRY_CODE.GR]: /^(?:6|$)(?:9|$)[0-9]{8}$/,
    [SITE_COUNTRY_CODE.CY]: /^9[45679][0-9]{6}$/,
    [SITE_COUNTRY_CODE.IE]: /^08[356789][0-9]{7}$/,
    [SITE_COUNTRY_CODE.PT]: /^9[1362][0-9]{7}$/
  },
  LAND_PHONE_REGEXP: {
    [SITE_COUNTRY_CODE.GR]: /^2[0-9]{9}$/, // Greece: Starts with '2' followed by 9 digits
    [SITE_COUNTRY_CODE.CY]: /^2[2-6][0-9]{6}$/, // Cyprus: Starts with '2' followed by 7 digits (e.g., 22XXXXXX)
    [SITE_COUNTRY_CODE.IE]: /^(0[1-9])[0-9]{7,8}$/, // Ireland: Starts with '0' followed by area code and 7-8 digits
    [SITE_COUNTRY_CODE.PT]: /^2[1-9][0-9]{7}$/ // Portugal: Starts with '2' followed by 8 digits (e.g., 21XXXXXX)
  },
  VAT_REGEXP: {
    [SITE_COUNTRY_CODE.GR]: /^[0-9]{9}$/,
    [SITE_COUNTRY_CODE.CY]: /^[0-9]{8}[A-Z]$/,
    [SITE_COUNTRY_CODE.IE]: /^[0-9][A-Z0-9][0-9]{5}[A-Z][A-Z]?$/,
    [SITE_COUNTRY_CODE.PT]: /^[0-9]{9}$/
  },
  FAV_ICON: {
    [SITE_COUNTRY_CODE.GR]: 'favicon',
    [SITE_COUNTRY_CODE.CY]: 'favicon',
    [SITE_COUNTRY_CODE.IE]: 'favicon_yourpro',
    [SITE_COUNTRY_CODE.PT]: 'favicon_yourpro'
  },
  FACEBOOK_AUTH_CLIENT_ID: {
    [SITE_COUNTRY_CODE.GR]: process.env.FACEBOOK_AUTH_CLIENT_ID,
    [SITE_COUNTRY_CODE.CY]: process.env.FACEBOOK_AUTH_CLIENT_ID_CY,
    [SITE_COUNTRY_CODE.IE]: process.env.FACEBOOK_AUTH_CLIENT_ID_IE,
    [SITE_COUNTRY_CODE.PT]: process.env.FACEBOOK_AUTH_CLIENT_ID_PT
  }
};

export const LANGUAGE_CONFIG = {
  LINKS: {
    HOME_PAGE: {
      [LANGUAGE_CODES.EL]: '/',
      [LANGUAGE_CODES.EL_CY]: '/',
      [LANGUAGE_CODES.EN]: '/en/',
      [LANGUAGE_CODES.EN_IE]: '/',
      [LANGUAGE_CODES.PT]: '/',
      [LANGUAGE_CODES.EN_PT]: '/en/'
    },
    TERMS_GENERAL: {
      [LANGUAGE_CODES.EL]: '/terms-general/',
      [LANGUAGE_CODES.EL_CY]: '/terms-general/',
      [LANGUAGE_CODES.EN]: '/en/terms-general/',
      [LANGUAGE_CODES.EN_IE]: '/terms-general/'
    },
    PRIVACY_POLICY: {
      [LANGUAGE_CODES.EL]: '/privacy-policy/',
      [LANGUAGE_CODES.EL_CY]: '/privacy-policy/',
      [LANGUAGE_CODES.EN]: '/en/privacy-policy/',
      [LANGUAGE_CODES.EN_IE]: '/privacy-policy/'
    },
    ANTI_CORRUPTION_POLICY: {
      [LANGUAGE_CODES.EL]: '/anti-corruption-policy/',
      [LANGUAGE_CODES.EL_CY]: '/anti-corruption-policy/',
      [LANGUAGE_CODES.EN]: '/en/anti-corruption-policy/',
      [LANGUAGE_CODES.EN_IE]: '/anti-corruption-policy/'
    }
  },
  MOMENT: {
    LOCALE: {
      [LANGUAGE_CODES.EL]: 'el',
      [LANGUAGE_CODES.EL_CY]: 'el',
      [LANGUAGE_CODES.EN]: 'en-us',
      [LANGUAGE_CODES.EN_IE]: 'en-ie',
      [LANGUAGE_CODES.PT]: 'pt'
    },
    DISPLAY_FORMAT: {
      FULL: {
        [LANGUAGE_CODES.EL]: 'D MMMM YYYY, HH:mm',
        [LANGUAGE_CODES.EL_CY]: 'D MMMM YYYY, HH:mm',
        [LANGUAGE_CODES.EN]: 'MMMM Do YYYY, h:mm a',
        [LANGUAGE_CODES.EN_IE]: 'MMMM Do YYYY, h:mm a',
        [LANGUAGE_CODES.PT]: 'D MMMM YYYY, HH:mm'
      }
    }
  },
  DATEPICKER: {
    DISPLAY_FORMAT: {
      DATE: {
        [LANGUAGE_CODES.EL]: 'dd/MM/yyyy',
        [LANGUAGE_CODES.EL_CY]: 'dd/MM/yyyy',
        [LANGUAGE_CODES.EN]: 'dd/MM/yyyy',
        [LANGUAGE_CODES.EN_IE]: 'dd/MM/yyyy',
        [LANGUAGE_CODES.PT]: 'dd/MM/yyyy'
      }
    }
  }
};

export enum STRIPE_ENV_KEY {
  SANDBOX = 'sandbox',
  PRODUCTION = 'production'
}

export const STRIPE_KEY = {
  [STRIPE_ENV_KEY.PRODUCTION]: {
    [SITE_COUNTRY_CODE.GR]: process.env.STRIPE_KEY_PROD_GR,
    [SITE_COUNTRY_CODE.CY]: process.env.STRIPE_KEY_PROD_CY,
    [SITE_COUNTRY_CODE.IE]: process.env.STRIPE_KEY_PROD_IE,
    [SITE_COUNTRY_CODE.PT]: process.env.STRIPE_KEY_PROD_PT
  },
  [STRIPE_ENV_KEY.SANDBOX]: {
    [SITE_COUNTRY_CODE.GR]: process.env.STRIPE_KEY_SANDBOX_GR,
    [SITE_COUNTRY_CODE.CY]: process.env.STRIPE_KEY_SANDBOX_CY,
    [SITE_COUNTRY_CODE.IE]: process.env.STRIPE_KEY_SANDBOX_IE,
    [SITE_COUNTRY_CODE.PT]: process.env.STRIPE_KEY_SANDBOX_PT
  }
};

export const SALES_FORCE_ORG_ID = {
  PROD: process.env.SALES_FORCE_ORG_ID_PROD,
  DEV: process.env.SALES_FORCE_ORG_ID_DEV
};
